import { MovieSession } from "../types/entities/movie"

export type SessionDayGroup = {
    date: string
    label: string
    sessions: MovieSession[]
}

function toLocalIsoDate(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}

/** DD.MM или DD.MM.YYYY из подписи вроде «Сегодня 18.06» */
function parseDotDate(raw: string): string | null {
    const match = raw.match(/(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?/)
    if (!match) return null

    const day = match[1].padStart(2, "0")
    const month = match[2].padStart(2, "0")
    let year = match[3]
    if (year) {
        if (year.length === 2) year = `20${year}`
    } else {
        year = String(new Date().getFullYear())
    }

    return `${year}-${month}-${day}`
}

/**
 * Нормализованный ключ YYYY-MM-DD для группировки и сортировки.
 * Приоритет у поля `date` из API («Сегодня 09.07»): ночные сеансы (00:00)
 * бэкенд относит к предыдущему дню, хотя календарно (datetime_at) это уже
 * следующая дата — их нельзя отрывать от группы своего дня.
 */
export function getSessionDateKey(session: MovieSession): string {
    if (session.date) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(session.date)) return session.date
        const parsed = parseDotDate(session.date)
        if (parsed) return parsed
    }

    if (session.datetime_at) {
        const iso = session.datetime_at.slice(0, 10)
        if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
        const parsed = parseDotDate(session.datetime_at)
        if (parsed) return parsed
    }

    if (session.datetime) {
        return toLocalIsoDate(new Date(session.datetime * 1000))
    }

    return session.date || "unknown"
}

function parseSessionDate(iso: string): Date {
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        return new Date(`${iso}T12:00:00`)
    }
    if (iso.includes(" ")) {
        return new Date(iso.replace(" ", "T"))
    }
    return new Date(iso)
}

export function formatSessionDayLabel(isoOrRaw: string): string {
    if (!isoOrRaw || isoOrRaw === "unknown") return "Сеансы"

    const iso = /^\d{4}-\d{2}-\d{2}$/.test(isoOrRaw) ? isoOrRaw : parseDotDate(isoOrRaw)
    if (!iso) return isoOrRaw

    const sessionDate = parseSessionDate(iso)
    if (Number.isNaN(sessionDate.getTime())) return isoOrRaw

    const today = new Date()
    today.setHours(12, 0, 0, 0)
    const day = new Date(sessionDate)
    day.setHours(12, 0, 0, 0)

    const diffDays = Math.round((day.getTime() - today.getTime()) / 86_400_000)
    if (diffDays === 0) return "Сегодня"
    if (diffDays === 1) return "Завтра"

    const weekday = sessionDate.toLocaleDateString("ru-RU", { weekday: "long" })
    const weekdayCap = weekday.charAt(0).toUpperCase() + weekday.slice(1)
    const shortDate = sessionDate.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })

    return `${weekdayCap} ${shortDate}`
}

function sessionSortTimestamp(session: MovieSession): number {
    if (session.datetime) return session.datetime
    if (session.datetime_at) {
        const t = new Date(session.datetime_at.replace(" ", "T")).getTime()
        if (!Number.isNaN(t)) return t / 1000
    }
    const key = getSessionDateKey(session)
    if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        return parseSessionDate(key).getTime() / 1000
    }
    return 0
}

export function groupMovieSessions(sessions: MovieSession[]): SessionDayGroup[] {
    const map = new Map<string, MovieSession[]>()

    for (const session of sessions) {
        const key = getSessionDateKey(session)
        const list = map.get(key) ?? []
        list.push(session)
        map.set(key, list)
    }

    return [...map.entries()]
        .sort(([a], [b]) => {
            if (a === "unknown") return 1
            if (b === "unknown") return -1
            return a.localeCompare(b)
        })
        .map(([date, items]) => ({
            date,
            label: formatSessionDayLabel(date),
            sessions: items.slice().sort((a, b) => sessionSortTimestamp(a) - sessionSortTimestamp(b) || a.time.localeCompare(b.time)),
        }))
}

export function shouldGroupSessionsByDate(sessions: MovieSession[], showDates?: boolean): boolean {
    if (showDates) return true
    const dates = new Set(sessions.map(getSessionDateKey).filter(key => key !== "unknown"))
    return dates.size > 1
}

/** Сеансы одного дня (для модалки покупки после выбора времени) */
export function filterSessionsForSessionId(sessions: MovieSession[], sessionId: number): MovieSession[] {
    const selected = sessions.find(session => session.id === sessionId)
    if (!selected) return sessions

    const dayKey = getSessionDateKey(selected)
    return sessions.filter(session => getSessionDateKey(session) === dayKey)
}
